/**
 * Serializes a value to JSON with object keys sorted at every depth, so that
 * two structurally equal values always produce the same string.
 *
 * Plain `JSON.stringify` is key-order sensitive, which makes it unsafe for
 * anything content-addressed: idempotency keys, change fingerprints, cache
 * lookups. Use this instead wherever a string has to stand in for a value's
 * identity.
 */
export function stableStringify(value: unknown): string {
  return JSON.stringify(stableNormalize(value));
}

function stableNormalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stableNormalize);
  }
  // Honour toJSON() the way JSON.stringify would, and do it *before* the
  // plain-object branch. A Date otherwise takes that branch, and
  // Object.keys(date) is empty, so every Date would collapse to {} — two
  // different dates producing one fingerprint, which silently skips changes to
  // a tracked date column.
  if (hasToJson(value)) {
    return stableNormalize(value.toJSON());
  }
  if (isPlainObject(value)) {
    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      normalized[key] = stableNormalize(value[key]);
    }
    return normalized;
  }
  return value;
}

function hasToJson(value: unknown): value is { toJSON: () => unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toJSON' in value &&
    typeof (value as { toJSON: unknown }).toJSON === 'function'
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray(value) === false
  );
}
